import type React from "react";

import { faInfo, faRotateRight, faTimer } from "@fortawesome/pro-regular-svg-icons";
import { faHashtag } from "@fortawesome/pro-solid-svg-icons";
import { t } from "i18next";

import type { LevelItem } from "api/services/content/schemas/exercises";
import {
  AboutContainer,
  Divider,
  IconText,
  InstructionCard,
  StyledBody,
  StyledH4,
  TipCard,
  Title,
} from "shared/atoms/PatientApp";
import { SmartphonePreview } from "shared/atoms/SmartphonePreview";

import type { PreviewTab } from "./ImageAndVideoContainer";
import { ImageAndVideoContainer } from "./ImageAndVideoContainer";

export type ExerciseLevelSubset = Pick<
  LevelItem,
  | "about"
  | "caption_file_url"
  | "image_url"
  | "instructions_icon_name"
  | "instructions_text1"
  | "instructions_text2"
  | "instructions_text3"
  | "reps"
  | "tip_description"
  | "tip_title"
  | "title"
  | "video_file_url"
  | "video_thumbnail_file_url"
  | "sets"
>;

interface Props {
  exercise: { levels: ExerciseLevelSubset[] };
  isLoading?: boolean;
  previewTab?: PreviewTab;
}

export const ExercisePreviewMobile: React.FC<Props> = ({ exercise, isLoading, previewTab }) => {
  const {
    about,
    caption_file_url,
    image_url,
    instructions_icon_name,
    instructions_text1,
    instructions_text2,
    instructions_text3,
    reps,
    sets,
    tip_description,
    tip_title,
    title,
    video_file_url,
    video_thumbnail_file_url,
  } = exercise.levels[0];
  const isTimedExercise = instructions_icon_name === "timer";

  return (
    <SmartphonePreview>
      <Title>{title}</Title>

      <ImageAndVideoContainer
        displayMediaTabs={false}
        isLoading={isLoading}
        image_url={image_url || ""}
        caption_file_url={caption_file_url || undefined}
        video_file_url={video_file_url || ""}
        video_thumbnail_file_url={video_thumbnail_file_url || ""}
        previewTab={previewTab}
      />

      <InstructionCard>
        {(instructions_text1 || instructions_text3) && (
          <>
            <IconText icon={faInfo} text={`${instructions_text1 || ""} ${instructions_text3 || ""}`} />
            <Divider />
          </>
        )}
        {isTimedExercise && <IconText icon={faTimer} text={`${reps} ${instructions_text2 || ""}`} />}
        {!isTimedExercise && <IconText icon={faRotateRight} text={`${reps} ${instructions_text2 || ""}`} />}
        {!!sets && sets > 1 && (
          <>
            <Divider />
            <IconText icon={faHashtag} text={t("library.exercises.fields.repeat_times", { sets })} />
          </>
        )}

        {tip_description && tip_title && <TipCard tip_description={tip_description} tip_title={tip_title} />}
      </InstructionCard>

      {about && (
        <AboutContainer>
          <StyledH4>{t("library.activity.exercise.about")}</StyledH4>
          <StyledBody>{about}</StyledBody>
        </AboutContainer>
      )}
    </SmartphonePreview>
  );
};
